/**
 * Provider global + hook que mantem contagem de mensagens nao-lidas do
 * WhatsApp em background, para alimentar o badge da sidebar.
 *
 * Estrategia:
 *  - Polling 10s via useWhatsappPolling (pausa em document.hidden).
 *  - So roda se o usuario tem `whatsapp:atender` (senao zero requests).
 *  - Endpoint usado: GET /api/whatsapp/conversas?status=ABERTA — soma
 *    `nao_lidas_count` de cada item. Backend ja filtra por unidade via
 *    scoped_queryset (Sprint 2 C5), entao o total reflete o que o
 *    usuario atual pode ver.
 *  - Em caso de falha, mantem ultimo total conhecido (nao zera) — UX
 *    estavel durante perda momentanea de rede.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { listarConversas } from "../services/whatsapp";
import useWhatsappPolling from "./useWhatsappPolling";
import { whatsappHabilitado } from "../utils/whatsappHabilitado";

const WhatsappNaoLidasContext = createContext({
  total: 0,
  porInstancia: {},
  atualizadoEm: null,
});

export function WhatsappNaoLidasProvider({ children }) {
  const { request, hasPermission, authenticated } = useAuth();
  const [estado, setEstado] = useState({
    total: 0,
    porInstancia: {},
    atualizadoEm: null,
  });

  // So roda polling se o modulo esta LIGADO + ha sessao + permissao.
  // Sem o gate, o ERP gastava 1 request a cada 10s por usuario
  // buscando conversas de um servico que nunca subiu (M-TRA-12).
  const ativo = whatsappHabilitado()
    && Boolean(authenticated)
    && hasPermission("whatsapp:atender");

  const carregar = useCallback(async () => {
    if (!ativo) return;
    try {
      const payload = await listarConversas(request, {
        status: "ABERTA",
        pageSize: 500,
      });
      const itens = Array.isArray(payload)
        ? payload
        : payload?.results || [];
      let total = 0;
      const porInstancia = {};
      itens.forEach((c) => {
        const naoLidas = Number(c.nao_lidas_count || 0);
        if (naoLidas > 0) {
          total += naoLidas;
          const inst = c.instance || c.instance_id || "default";
          porInstancia[inst] = (porInstancia[inst] || 0) + naoLidas;
        }
      });
      setEstado({ total, porInstancia, atualizadoEm: new Date() });
    } catch (_err) {
      // Mantem ultimo estado. Auto-toast cobre warning.
    }
  }, [ativo, request]);

  // Polling 10s. Hook ja cobre pausa em background.
  // Bloco LOG2 Camada D (2026-05-25): atrasa 2s o primeiro tick para
  // reduzir simultaneidade na cascata pos-login. Polling 10s eh mais
  // urgente que o sino (60s), por isso usa delay menor.
  useWhatsappPolling(carregar, ativo ? 10000 : 0, [ativo], { delayPrimeiroMs: 2000 });

  const value = useMemo(
    () => ({ ...estado, atualizar: carregar }),
    [estado, carregar],
  );

  return (
    <WhatsappNaoLidasContext.Provider value={value}>
      {children}
    </WhatsappNaoLidasContext.Provider>
  );
}

export function useWhatsappNaoLidasGlobal() {
  return useContext(WhatsappNaoLidasContext);
}
